import { useState } from 'react';
import { vehicleUrlParam } from '../utils/vehicleUrl';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Button from 'react-bootstrap/Button';
import Image from 'react-bootstrap/Image';
import Carousel from 'react-bootstrap/Carousel';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';


import {
  RiCalendar2Line,
  RiSettingsLine,
  RiExpandRightLine,
  RiFacebookCircleFill,
  RiWhatsappFill,
  RiGasStationLine,
  RiMoneyDollarCircleLine,
} from "react-icons/ri";
import Row from "react-bootstrap/esm/Row";
import Col from "react-bootstrap/esm/Col";

function ProviderBadge({ name, bgColor, textColor }) {
  if (!name) return null;
  return (
    <span style={{
      display: 'inline-block',
      background: bgColor || '#555555', color: textColor || '#ffffff',
      fontSize: 11, fontWeight: 700,
      padding: '4px 10px', borderRadius: 20,
      letterSpacing: '.3px',
    }}>
      {name}
    </span>
  );
}

function formatAdminDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? '—' : d.toLocaleString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function ResultEntry({ entry, onBeforeNavigate }) {

  const navigate = useNavigate();
  const { role } = useAuth() || {};
  const isAdmin = role === 'ADMIN';
  const [imageIndex, setImageIndex] = useState(0);
  const handleImageSelect = (selectedIndex) => {
    setImageIndex(selectedIndex);
  };

  const fragmentCarInformationKm = <div className='result-info'>
    <><b>Kilometraje</b></><br />
    <span>{entry.kilometers} Kms.</span><br />
  </div>

  const shareUrl = `${window.location.origin}/vehiculo/${vehicleUrlParam(entry)}`;
  const shareText = encodeURIComponent(`${entry.title} - Solo Autos`);

  const fragmentShare = <>
    <span>Compartir</span><br />
    <span>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" style={{ color: '#1877F2' }}>
        <RiFacebookCircleFill size={20} />
      </a>
      &nbsp;&nbsp;&nbsp;
      <a href={`https://wa.me/?text=${shareText}%20${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" style={{ color: '#25D366' }}>
        <RiWhatsappFill size={20} />
      </a>
    </span>
  </>

  const handleContactClick = () => {
    onBeforeNavigate?.();
    navigate(`/vehiculo/${vehicleUrlParam(entry)}`, { state: entry });
  };

  // Tooltip para el botón de contactar
  const contactTooltip = (props) => (
    <Tooltip id="contact-tooltip" {...props}>
      Ver detalle del vehículo
    </Tooltip>
  );

  const fragmentBtnContact = <>
    <OverlayTrigger
      placement="top"
      delay={{ show: 250, hide: 400 }}
      overlay={contactTooltip}
    >
      <Button
        variant="primary"
        onClick={handleContactClick}
      >
        Ver detalle <RiExpandRightLine size={20} />
      </Button>
    </OverlayTrigger><br />
  </>

  const formatPrice = (price) => {
    if (!price) return null;
    return '$' + Number(price).toLocaleString('es-CL');
  };

  // Solo visible para ADMIN. Publicaciones propias (marketplace) no tienen estas fechas.
  const fragmentAdminDates = isAdmin && (entry.importedAt || entry.lastSeenAt) ? (
    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
      <span title="Primera vez que se importó la publicación">
        <b>Importado:</b> {formatAdminDate(entry.importedAt)}
      </span>
      {' · '}
      <span title="Fecha del último proceso de importación que encontró esta publicación">
        <b>Actualizado:</b> {formatAdminDate(entry.lastSeenAt)}
      </span>
    </div>
  ) : null;

  const fragmentCarInformation = <>
    <span><RiCalendar2Line />Año {entry.year}</span>
    {entry.transmission && <span><RiSettingsLine />{entry.transmission}</span>}
    {entry.fuelType && <span><RiGasStationLine />{entry.fuelType}</span>}
    {entry.price && <span><RiMoneyDollarCircleLine />{formatPrice(entry.price)}</span>}
  </>


  return (
    <div className="result-entry " >
      {/* HEADER */}
      <Row className="result-header align-items-center">
        {/* Desktop */}
        <Col md={11} className="d-none d-md-block">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <h5 style={{ cursor: 'pointer', margin: 0 }} onClick={handleContactClick}><b>{entry.title}</b></h5>
            <ProviderBadge name={entry.url?.startsWith('/publicar/') ? 'SoloAutos' : entry.providerName} bgColor={entry.providerBgColor} textColor={entry.providerTextColor} />
          </div>
          {fragmentCarInformation}
          {fragmentAdminDates}
        </Col>

        {/* Mobile */}
        <Col xs={12} className="d-block d-md-none">
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
            <h5 style={{ color: 'black', cursor: 'pointer', margin: 0 }} onClick={handleContactClick}><b>{entry.title}</b></h5>
            <ProviderBadge name={entry.url?.startsWith('/publicar/') ? 'SoloAutos' : entry.providerName} bgColor={entry.providerBgColor} textColor={entry.providerTextColor} />
          </div>
          {fragmentCarInformation}
          {fragmentAdminDates}
        </Col>
      </Row>

      {/* DESKTOP VIEW */}
      <div className="d-none d-lg-block">
        <Row>
          <Col lg={8} style={{ marginBottom: '15px' }}>
            <Carousel activeIndex={imageIndex} onSelect={handleImageSelect} indicators={false} className="shadow">
              {entry.images.map((item, key) => (
                <Carousel.Item key={key}>
                    <Image
                      src={item.url}
                      alt={item.alt || "Imagen del vehículo"}
                      fluid
                      style={{ cursor: 'pointer' }}
                      onClick={handleContactClick}
                      onError={(e) => {
                        console.warn(`Imagen no encontrada: ${item.url}`);
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiBmaWxsPSIjOTk5OTk5IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkltYWdlbiBubyBkaXNwb25pYmxlPC90ZXh0Pgo8L3N2Zz4=';
                      }}
                    />
                </Carousel.Item>
              ))}
            </Carousel>
          </Col>

          <Col style={{ position: 'relative' }}>
            <Row>
              <h6><b>Resumen del Vehículo</b></h6>
              <span>{entry.summary?.length > 150 ? entry.summary.substring(0, 150) + '...' : entry.summary}</span>
            </Row>
            <Row>
              <Col>{fragmentCarInformationKm}
              </Col>
            </Row>
            <Row>
              <Col className="container-contact">
                <div>
                  {fragmentBtnContact}
                  {fragmentShare}
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>

      {/* MOBILE VIEW */}
      <div className="d-block d-lg-none">
        <Row>
          <Col xs={12} >
              <Carousel activeIndex={imageIndex} onSelect={handleImageSelect} indicators={false} className="shadow ">
                {entry.images.map((item, key) => (
                  <Carousel.Item key={key}>
                    <Image 
                      src={item.url} 
                      alt={item.alt || "Imagen del vehículo"} 
                      fluid 
                      onError={(e) => {
                        console.warn(`Imagen no encontrada: ${item.url}`);
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgdmlld0JveD0iMCAwIDQwMCAzMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iMzAwIiBmaWxsPSIjRjVGNUY1Ii8+Cjx0ZXh0IHg9IjIwMCIgeT0iMTUwIiBmaWxsPSIjOTk5OTk5IiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMTQiIHRleHQtYW5jaG9yPSJtaWRkbGUiPkltYWdlbiBubyBkaXNwb25pYmxlPC90ZXh0Pgo8L3N2Zz4=';
                      }}
                    />
                  </Carousel.Item>
                ))}
              </Carousel>
          </Col>
    
          <Col xs={12}>
            <h6><b>Resumen del Vehículo</b></h6>
            <span>{entry.summary?.length > 150 ? entry.summary.substring(0, 150) + '...' : entry.summary}</span>
          </Col>
          <Col xs={6}>{fragmentCarInformationKm}</Col>
          <Col xs={6} className="container-contact-xs">
            <div>
              {fragmentBtnContact}
              {fragmentShare}
            </div>
          </Col>
        </Row>
      </div>
    </div >
  )
};